import { Card, Icon, Tag, Text } from "@ttc3k/trekker";
import { Box, BoxProps, Flex, styled } from "styled-system/jsx";
import { Infinite, Repeat } from "iconoir-react";
import { RefObject, useRef } from "react";
import { AssetImage } from "gql/generated";
import { SmartImage } from "shared/components/atoms";
import {
  widgetTeaserCardRecipe,
  WidgetTeaserCardRecipeProps,
} from "./recipe";

/** What follows the dates in the tag: a repeat icon, an ellipsis, or an infinity icon. */
export type WidgetTeaserCardDateTrailing = "repeat" | "ellipsis" | "infinite";

export type WidgetTeaserCardDateTag = {
  /** First date, e.g. "Thu, Oct 15" or "Oct 19 - Oct 23" or "Ongoing". */
  start: string;
  /** Further day numbers in the same month; at most two are shown, then "...". */
  others?: string[];
  trailing?: WidgetTeaserCardDateTrailing;
};

export type WidgetTeaserCardProps = WidgetTeaserCardRecipeProps & {
  title: string;
  /** Real href on the title (crawlable); the click handlers decide what happens. */
  titleHref?: string;
  titleLinkTarget?: string;
  titleLinkRel?: string;
  onTitleLinkClick?: (e: React.MouseEvent) => void;
  onTitleLinkKeyUp?: (e: React.KeyboardEvent) => void;
  /** Second line under the title (venue, listing name). */
  subtitle?: string;
  dateTag: WidgetTeaserCardDateTag;
  /** Optional status tag rendered before the date tag (e.g. "Cancelled"). */
  statusTag?: { label: string; part?: string };
  heroImage?: Partial<AssetImage> | null;
  heroAlt?: string;
  /** Show a video preview for video assets. */
  heroPreviewVideo?: boolean;
  /** Ref for the card surface (impression tracking); also used to size the hero. */
  cardRef?: RefObject<HTMLDivElement | null>;
  ariaLabel?: string;
  cardSurfaceProps?: BoxProps;
  onClick?: () => void;
  onKeyUp?: (e: React.KeyboardEvent) => void;
};

/**
 * Crawlable anchor around the card title so search engines can discover the
 * hosted pages. Resets keep the existing title appearance; the
 * `card__title-link` part is additive for customer theming.
 */
const TitleLink = styled("a", {
  base: {
    display: "block",
    color: "inherit",
    textDecoration: "none",
  },
});

/**
 * The widget teaser card shared by the Events and Promotions widgets: a date
 * tag, a two-line title, a subtitle and a hero on the right (landscape) or
 * on top (portrait). Purely presentational; the widgets provide the data,
 * the tracking and the click behaviour. The `card__*` parts are customer
 * theming hooks and must stay stable.
 */
export const WidgetTeaserCard = ({
  orientation,
  title,
  titleHref,
  titleLinkTarget,
  titleLinkRel,
  onTitleLinkClick,
  onTitleLinkKeyUp,
  subtitle,
  dateTag,
  statusTag,
  heroImage,
  heroAlt,
  heroPreviewVideo = false,
  cardRef,
  ariaLabel,
  cardSurfaceProps,
  onClick,
  onKeyUp,
}: WidgetTeaserCardProps) => {
  const classes = widgetTeaserCardRecipe({ orientation });
  const isLandscapeOrientation = orientation === "landscape";
  const internalRef = useRef<HTMLDivElement | null>(null);
  const measureRef = cardRef ?? internalRef;

  const getDynamicHeroWidth = () => {
    const cardWidth = measureRef.current?.clientWidth ?? 0;
    return isLandscapeOrientation ? 160 : Math.max(280, cardWidth + 2);
  };

  const others = dateTag.others ?? [];

  const titleText = (
    <Text
      part={"card__title"}
      visual={"bodySemiBold"}
      lineClamp={2}
      wordBreak="break-word"
      color={"text.dark"}
      h="48px"
      role="heading"
      aria-level={3}
    >
      {title}
    </Text>
  );

  return (
    <Card.Surface
      ref={measureRef}
      part={"card__surface"}
      className={classes.surface}
      tabIndex="0"
      role="article"
      aria-label={ariaLabel ?? title}
      {...cardSurfaceProps}
      onClick={onClick}
      onKeyUp={onKeyUp}
    >
      <Card.Body part={"card__body"} className={classes.body}>
        <Flex part={"card___tag_and_title"} className={classes.dateAndTitle}>
          <Flex flexDir="row" alignItems="center" gap="100" flexWrap="wrap">
            {statusTag && (
              <Tag
                part={statusTag.part ?? "card__tag_cancelled"}
                visual="error"
                mode="light"
                bg="bg.error.light"
                color="text.error.dark"
              >
                {statusTag.label}
              </Tag>
            )}
            <Tag part={"card__tag"} visual="primary" mode="light">
              <styled.span
                display="flex"
                flexDir="row"
                alignItems="center"
                gap="100"
              >
                <span>
                  {dateTag.start}
                  {others.length > 0 && (
                    <span>
                      {others.slice(0, 2).map(date => (
                        <span key={date}>
                          <styled.span color="text.light"> • </styled.span>
                          {date}
                        </span>
                      ))}
                      {others.length > 2 && "..."}
                    </span>
                  )}
                </span>
                {dateTag.trailing === "repeat" ? (
                  <Icon Element={Repeat} />
                ) : dateTag.trailing === "infinite" ? (
                  <Icon Element={Infinite} />
                ) : dateTag.trailing === "ellipsis" ? (
                  <span style={{ marginLeft: "-4px" }}>...</span>
                ) : null}
              </styled.span>
            </Tag>
          </Flex>

          {titleHref ? (
            <TitleLink
              part={"card__title-link"}
              href={titleHref}
              target={titleLinkTarget}
              rel={titleLinkRel}
              onClick={onTitleLinkClick}
              onKeyUp={onTitleLinkKeyUp}
            >
              {titleText}
            </TitleLink>
          ) : (
            titleText
          )}
        </Flex>

        <Text
          part={"card__venue"}
          visual={"smallSemiBold"}
          lineClamp="1"
          wordBreak="break-all"
          color={"text.mid"}
          justifySelf="flex-end"
        >
          {subtitle}
        </Text>
      </Card.Body>
      <Box part={"card__hero"} className={classes.hero}>
        <SmartImage
          image={heroImage ?? undefined}
          width={isLandscapeOrientation ? 160 : getDynamicHeroWidth()}
          height={isLandscapeOrientation ? 160 : 200}
          shape="square"
          previewVideo={heroPreviewVideo}
          alt={heroAlt ?? title}
          parts={{
            container: "card__image-container",
            frame: "card__image-frame",
            image: "card__image",
          }}
        />
      </Box>
    </Card.Surface>
  );
};
